import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { RecordModel } from "pocketbase";
import { pb, queryClient } from "@rekonas/data-access";

interface AuthContextValue {
  user: RecordModel | null;
  isAuthenticated: boolean;
  isRestoring: boolean;
  login: (identity: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function clearProtectedClientState(): void {
  void queryClient.cancelQueries();
  queryClient.clear();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<RecordModel | null>(() => pb.authStore.record);
  const [isRestoring, setIsRestoring] = useState(true);

  useEffect(() => {
    let alive = true;

    const syncUser = () => {
      if (alive) setUser(pb.authStore.isValid ? pb.authStore.record : null);
    };
    const unsubscribe = pb.authStore.onChange(syncUser);
    const invalidate = () => {
      clearProtectedClientState();
      pb.authStore.clear();
      syncUser();
    };
    window.addEventListener("pb-auth-invalid", invalidate);

    async function restore() {
      if (!pb.authStore.isValid) {
        if (alive) {
          setUser(null);
          setIsRestoring(false);
        }
        return;
      }
      try {
        await pb.collection("users").authRefresh();
        syncUser();
      } catch {
        clearProtectedClientState();
        pb.authStore.clear();
        syncUser();
      } finally {
        if (alive) setIsRestoring(false);
      }
    }

    void restore();
    return () => {
      alive = false;
      unsubscribe();
      window.removeEventListener("pb-auth-invalid", invalidate);
    };
  }, []);

  const login = useCallback(async (identity: string, password: string) => {
    clearProtectedClientState();
    pb.authStore.clear();
    setUser(null);
    const result = await pb.collection("users").authWithPassword(identity.trim(), password);
    setUser(result.record);
  }, []);

  const logout = useCallback(() => {
    clearProtectedClientState();
    pb.authStore.clear();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, isAuthenticated: Boolean(user && pb.authStore.isValid), isRestoring, login, logout }),
    [user, isRestoring, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}
